import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, Typography,   
    CircularProgress, TextField, Button,  
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const ParamPage = () => {
    useAuthCheck();
    const { paramId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formValues, setFormValues] = useState({
        param: '',
        value: '',
        description: '',
    });

    
    useEffect(() => {
        const fetchParam = async () => {
            try {
                setLoading(true);

                if (paramId) {
                    const response = await api.get(`/params/${paramId}`);

                    const formData = response?.data || {};
                    setFormValues({
                        param: formData.param || '',
                        value: formData.value || '',
                        description: formData.description || '',
                    })
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchParam();
    }, [paramId]);

    const checkoutSchema = yup.object().shape({
        param: yup.string().required('Required'),
        value: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = paramId ? "PUT" : "POST";
            const url = paramId
                ? `/params/update/${paramId}` 
                : '/params/new'

            await api({
                method,
                url,
                data: values,
            });
            
            toast.success(paramId 
                ? 'Param updated successfully.' 
                : 'Param created successfully.'
            );

            if (!paramId) {
                onSubmitProps.resetForm();
            }

            navigate("/params");            
        } catch (error) {
            setError(error);
            toast.error(error?.response?.data?.message || "Failed to save param!");
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Param"
                    subtitle={`${loading ? 'Loading...' : paramId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/params")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>                
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Param"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.param}
                                        name="param"
                                        error={!!touched.param && !!errors.param}
                                        helperText={touched.param && errors.param}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        multiline
                                        rows={2}
                                        type="text"
                                        label="Value"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.value}
                                        name="value"
                                        error={!!touched.value && !!errors.value}
                                        helperText={touched.value && errors.value}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={3}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {paramId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default ParamPage;
